
import Button from '../../../components/base/Button';

export default function HostingExperienceSection() {
  const properties = [
    {
      location: "Greenwich, CT",
      count: "3 Rentals",
      type: "Luxury Rentals",
      icon: "ri-home-2-fill",
      description: "Premium short, mid, and long term rentals in prestigious Greenwich"
    },
    {
      location: "Port Chester, NY",
      count: "1 Rental", 
      type: "Urban Retreat",
      icon: "ri-home-4-fill",
      description: "Comfortable short, mid, and long term rental in Port Chester"
    },
    {
      location: "New York City",
      count: "3 Rentals",
      type: "City Experience", 
      icon: "ri-building-4-fill",
      description: "Modern NYC short, mid, and long term rentals in prime location"
    },
    {
      location: "Big Sky, Montana",
      count: "1 Rental",
      type: "Alpine Luxury",
      icon: "ri-landscape-fill",
      description: "Exquisite ski-in/ski-out short-term rental in prestigious Big Sky"
    },
    {
      location: "International",
      count: "3 Rentals",
      type: "Global Portfolio",
      icon: "ri-earth-fill",
      description: "Overseas short, mid and long-term rental properties expanding our reach"
    }
  ];
  const stats = [
    { number: "13", label: "Years Hosting", icon: "ri-calendar-check-fill" },
    { number: "11", label: "Active Rentals", icon: "ri-home-heart-fill" },
    { number: "99%", label: "Super Host Rate", icon: "ri-star-fill" },
    { number: "$15M+", label: "Property Value", icon: "ri-money-dollar-circle-fill" }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-blue-50 via-white to-emerald-50 relative overflow-hidden">
      {/* Bright background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-br from-emerald-100 to-blue-100 rounded-full blur-3xl opacity-60"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-blue-50/30 to-emerald-50/30"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-blue-500 text-white rounded-full text-sm font-semibold mb-8 shadow-lg">
            <i className="ri-award-fill mr-2"></i>
            Proven Track Record
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            We Practice What We Preach
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            With 13 years of hosting experience and nearly perfect Super Host status, we manage $4.8M worth of short-term and mid-term rentals, and long-term rental properties valued at $10.2M, giving us deep expertise in both markets and understanding of the unique challenges and opportunities each presents.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {stats.map((stat, index) => (
            <div key={index} className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transform group-hover:scale-105 transition-all duration-300">
                <i className={`${stat.icon} text-3xl text-white`}></i>
              </div>
              <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent mb-2">{stat.number}</div>
              <div className="text-slate-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Properties Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-16">
          {properties.map((property, index) => (
            <div key={index} className="group bg-white/80 backdrop-blur-sm rounded-xl p-8 hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border border-white/50 hover:border-emerald-200 flex flex-col h-full shadow-lg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:shadow-xl transform group-hover:scale-105 transition-all duration-300">
                  <i className={`${property.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{property.location}</h3>
                <div className="text-emerald-600 font-semibold text-sm mb-2">{property.count}</div>
                <div className="text-blue-600 text-sm font-medium mb-3">{property.type}</div>
              </div>
              <p className="text-slate-600 text-center text-sm flex-grow">{property.description}</p>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="relative">
          <div 
            className="rounded-3xl p-16 text-center relative overflow-hidden bg-cover bg-center shadow-2xl"
            style={{
              backgroundImage: `linear-gradient(135deg, rgba(16, 185, 129, 0.9), rgba(59, 130, 246, 0.8)), url('https://public.readdy.ai/ai/img_res/75887daab3132ced668302d1dff41d9e.jpg')`
            }}
          >
            {/* Bright overlay for better text contrast */}
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-3xl"></div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center px-6 py-3 bg-white/95 backdrop-blur-sm text-slate-900 rounded-full text-sm font-bold mb-8 shadow-lg">
                <i className="ri-external-link-fill mr-2 text-emerald-600"></i>
                SEE OUR PROPERTIES
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Experience Our Hospitality Yourself
              </h3>
              <p className="text-white/95 mb-10 text-lg max-w-2xl mx-auto leading-relaxed">
                Don't just take our word for it. Visit our Airbnb listings to see our properties, read guest reviews, and experience the quality that has earned us Super Host status for nearly our entire hosting journey.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <Button 
                  size="lg" 
                  className="bg-slate-900 text-white hover:bg-slate-800 font-bold shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  onClick={() => window.REACT_APP_NAVIGATE('/rentals')}
                >
                  <i className="ri-airbnb-fill mr-2 text-red-500"></i>
                  View Our Airbnb Properties
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 backdrop-blur-sm bg-white/10 shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  onClick={() => window.REACT_APP_NAVIGATE('/rentals')}
                >
                  <i className="ri-star-fill mr-2 text-yellow-400"></i>
                  Read Guest Reviews
                </Button>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pt-8 border-t border-white/30">
                <div className="flex items-center text-white/95 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <i className="ri-shield-check-fill text-emerald-300 mr-2"></i>
                  <span className="text-sm font-medium">Nearly Perfect Super Host</span>
                </div>
                <div className="flex items-center text-white/95 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <i className="ri-customer-service-2-fill text-blue-300 mr-2"></i>
                  <span className="text-sm font-medium">24/7 Guest Support</span>
                </div>
                <div className="flex items-center text-white/95 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <i className="ri-medal-fill text-yellow-300 mr-2"></i>
                  <span className="text-sm font-medium">13 Years Experience</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
